import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { ArrowLeft, Loader2, Mic, Pause, Play, Sparkles, Square, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { startBackgroundStickerCreate } from "@/lib/background-sticker-create";
import { supabase } from "@/integrations/supabase/client";
import {
  getStickerDraft,
  resetStickerDraft,
  setStickerAudio,
  setStickerField,
  setStickerFile,
  setStickerSaveError,
  setStickerSaveQueued,
  subscribeStickerDraft,
} from "@/lib/sticker-create-draft";
import { EXPRESSION_EVENTS, listenExpressionEvent } from "@/lib/expressionActions";

const SUPPORTED_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);

function parseTagList(raw: string) {
  return raw
    .split(/[,\s]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

function formatTagList(tags: string[]) {
  return Array.from(new Set(tags.map((t) => t.trim().toLowerCase()).filter(Boolean))).join(", ");
}

async function assertImageDecodes(file: File) {
  if (typeof createImageBitmap !== "function") return;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    if (bitmap.width < 1 || bitmap.height < 1) {
      throw new Error("Image has no visible pixels");
    }
  } finally {
    bitmap?.close();
  }
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
}

/**
 * Route-level full-screen sticker creator.
 *
 * Deliberately does NOT use Radix Sheet/Dialog primitives. On mobile the
 * native file picker round-trip can fire focus/outside-interaction events
 * that close dialog-based UIs, dropping the freshly-picked file before save.
 *
 * Image + form state lives in a module-level store (sticker-create-draft.ts)
 * so it survives any subtree remount caused by the native picker.
 */
export default function StickerCreateSheet({ open, onOpenChange, onCreated }: Props) {
  const draft = useSyncExternalStore(subscribeStickerDraft, getStickerDraft, getStickerDraft);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(false);

  // Voice recorder local state (the resulting blob is persisted in the draft store).
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startTsRef = useRef<number>(0);
  const stopTimerRef = useRef<number | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordElapsed, setRecordElapsed] = useState(0);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const MAX_AUDIO_MS = 5000;
  const selectedTags = useMemo(() => new Set(parseTagList(draft.tags)), [draft.tags]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData?.user?.id;
        if (!userId) return;

        const { data, error } = await supabase
          .from("user_stickers")
          .select("tags")
          .eq("user_id", userId)
          .limit(200);
        if (error || cancelled) return;

        const seen = new Set<string>();
        for (const row of data ?? []) {
          const tags = Array.isArray((row as any).tags) ? (row as any).tags : [];
          for (const tag of tags) {
            const clean = String(tag).trim().toLowerCase();
            if (clean) seen.add(clean);
          }
        }
        setAvailableTags(Array.from(seen).slice(0, 32));
      } catch (err) {
        console.warn("[StickerCreate] could not load tag suggestions", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Mount/unmount logging.
  useEffect(() => {
    if (!open) return;
    console.log("[StickerCreate] mounted (open)", { hasFile: !!draft.file, hasPreview: !!draft.objectUrl });
    return () => {
      console.log("[StickerCreate] unmounted while open=", open, "draftFile=", !!getStickerDraft().file);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFile = useCallback(async (f: File) => {
    const cur = getStickerDraft();
    if (cur.saveQueued) return;
    console.log("[StickerCreate] file selected", { name: f.name, type: f.type, size: f.size });
    if (!f.type.startsWith("image/")) { toast("Please pick an image"); return; }
    if (!SUPPORTED_TYPES.has(f.type)) { toast("Please use PNG, JPG, WEBP, or GIF"); return; }
    if (f.size > 5 * 1024 * 1024) { toast("Image must be under 5MB"); return; }
    try {
      await assertImageDecodes(f);
    } catch (err) {
      console.warn("[StickerCreate] image decode failed", err);
      toast("That image could not be read. Please try another one.");
      return;
    }
    setStickerFile(f);
  }, []);

  const openPicker = useCallback(() => {
    console.log("[StickerCreate] opening native file picker");
    fileInputRef.current?.click();
  }, []);

  const clearImage = useCallback(() => {
    if (draft.saveQueued) return;
    setStickerFile(null);
  }, [draft.saveQueued]);

  const toggleTag = useCallback((tag: string) => {
    const current = parseTagList(getStickerDraft().tags);
    const next = current.includes(tag)
      ? current.filter((t) => t !== tag)
      : [...current, tag];
    setStickerField("tags", formatTagList(next));
  }, []);

  // Listen for background save lifecycle, scoped by requestId.
  useEffect(() => {
    const removeCreated = listenExpressionEvent(EXPRESSION_EVENTS.stickerCreated, (detail) => {
      const cur = getStickerDraft().activeRequestId;
      if (cur && detail?.requestId && detail.requestId !== cur) return;
      console.log("[StickerCreate] save success");
      resetStickerDraft();
      onCreated?.();
      onOpenChange(false);
    });
    const removeFailed = listenExpressionEvent(EXPRESSION_EVENTS.stickerCreateFailed, (detail) => {
      const cur = getStickerDraft().activeRequestId;
      if (cur && detail?.requestId && detail.requestId !== cur) return;
      console.warn("[StickerCreate] save failed", detail?.error);
      setStickerSaveError(detail?.error || "Save failed");
    });
    return () => {
      removeCreated();
      removeFailed();
    };
  }, [onCreated, onOpenChange]);

  const handleSave = useCallback(() => {
    const cur = getStickerDraft();
    if (cur.saveQueued) return;
    if (!cur.file) { toast("Pick an image first"); return; }
    if (!cur.tags.trim()) { toast("Add at least one tag so you can find it later"); return; }
    console.log("[StickerCreate] save clicked", { name: cur.file.name, size: cur.file.size, requestId: cur.activeRequestId });
    const reqId = startBackgroundStickerCreate({
      blob: cur.file,
      filename: cur.file.name || "sticker.png",
      packName: cur.packName,
      tagsRaw: cur.tags,
      isPublic: cur.isPublic,
      audioBlob: cur.audioBlob,
      audioDurationMs: cur.audioDurationMs,
      audioTranscript: cur.audioTranscript,
      clientRequestId: cur.activeRequestId,
    });
    setStickerSaveQueued(true, reqId ?? null);
    console.log("[StickerCreate] background request started", reqId);
  }, []);

  const handleClose = useCallback(() => {
    if (draft.saveQueued) {
      toast("Saving sticker - please wait");
      return;
    }
    onOpenChange(false);
  }, [draft.saveQueued, onOpenChange]);

  // Tick recording timer + auto-stop at MAX_AUDIO_MS.
  useEffect(() => {
    if (!isRecording) return;
    const id = window.setInterval(() => {
      const elapsed = Date.now() - startTsRef.current;
      setRecordElapsed(elapsed);
      if (elapsed >= MAX_AUDIO_MS) {
        try { recorderRef.current?.stop(); } catch { /* ignore */ }
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const elapsed = Date.now() - startTsRef.current;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        if (!mountedRef.current) return;
        if (blob.size > 0) setStickerAudio(blob, Math.min(elapsed, MAX_AUDIO_MS), "");
        setIsRecording(false);
        setRecordElapsed(0);
      };
      startTsRef.current = Date.now();
      rec.start();
      recorderRef.current = rec;
      setIsRecording(true);
    } catch (err: any) {
      console.error("[StickerCreate] mic error", err);
      toast("Microphone permission required");
    }
  }, []);

  const stopRecording = useCallback(() => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
  }, []);

  const removeAudio = useCallback(() => {
    if (previewAudioRef.current) {
      try { previewAudioRef.current.pause(); } catch { /* ignore */ }
    }
    setIsPreviewPlaying(false);
    setStickerAudio(null);
  }, []);

  const togglePreview = useCallback(() => {
    const a = previewAudioRef.current;
    if (!a) return;
    if (isPreviewPlaying) {
      a.pause();
      setIsPreviewPlaying(false);
    } else {
      a.currentTime = 0;
      a.play().then(() => setIsPreviewPlaying(true)).catch(() => setIsPreviewPlaying(false));
    }
  }, [isPreviewPlaying]);

  // Cleanup on unmount.
  useEffect(() => () => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-background flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      role="dialog"
      aria-label="Create a sticker"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-2 py-2 border-b border-border">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-xl"
          onClick={handleClose}
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2 text-base font-semibold">
          <Sparkles className="h-4 w-4 text-primary" />
          Create a sticker
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!draft.file ? (
          <button
            type="button"
            onClick={openPicker}
            className="w-full h-48 rounded-2xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-accent/30 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground"
          >
            <Sparkles className="h-8 w-8 text-primary" />
            <span className="text-sm font-medium">Pick an image</span>
            <span className="text-xs">PNG, JPG, WEBP, or GIF · max 5MB</span>
            <span className="text-[11px]">Background is removed automatically after saving</span>
          </button>
        ) : (
          <div className="flex gap-3 items-start">
            <div className="relative h-32 w-32 rounded-xl flex-shrink-0">
              <div className="h-full w-full rounded-xl bg-[conic-gradient(at_50%_50%,_hsl(var(--muted))_25%,_transparent_25%,_transparent_50%,_hsl(var(--muted))_50%,_hsl(var(--muted))_75%,_transparent_75%)] bg-[length:12px_12px] flex items-center justify-center p-1 overflow-hidden">
                {draft.objectUrl && (
                  <img
                    src={draft.objectUrl}
                    alt="sticker preview"
                    className="max-w-full max-h-full object-contain"
                  />
                )}
              </div>
              <button
                type="button"
                onClick={clearImage}
                disabled={draft.saveQueued}
                className="absolute right-1 top-1 h-7 w-7 rounded-full bg-destructive text-destructive-foreground shadow-sm flex items-center justify-center disabled:opacity-50"
                title="Remove image"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <input
                type="text"
                value={draft.packName}
                onChange={(e) => setStickerField("packName", e.target.value)}
                placeholder="Pack name (e.g. Team Vibes)"
                className="w-full h-10 px-3 rounded-lg bg-secondary text-sm border-0 outline-none focus:ring-1 focus:ring-ring"
              />
              <input
                type="text"
                value={draft.tags}
                onChange={(e) => setStickerField("tags", e.target.value)}
                placeholder="Tags (e.g. happy, dog, lol)"
                className="w-full h-10 px-3 rounded-lg bg-secondary text-sm border-0 outline-none focus:ring-1 focus:ring-ring"
              />
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto pr-1">
                {availableTags.map((tag) => {
                  const active = selectedTags.has(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={
                        active
                          ? "rounded-full bg-primary text-primary-foreground px-2.5 py-1 text-[11px] font-medium"
                          : "rounded-full bg-secondary text-muted-foreground hover:bg-accent hover:text-foreground px-2.5 py-1 text-[11px] font-medium"
                      }
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isPublic}
                  onChange={(e) => setStickerField("isPublic", e.target.checked)}
                  className="rounded"
                />
                Share with the community
              </label>
            </div>
          </div>
        )}

        {draft.saveError && (
          <p className="text-xs text-destructive leading-snug">
            {draft.saveError} — your image and tags are kept. Tap Save to retry.
          </p>
        )}

        {draft.file && (
          <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium flex items-center gap-1.5">
                <Mic className="h-3.5 w-3.5 text-primary" />
                Voice (optional · max 5s)
              </div>
              {draft.audioBlob && !isRecording && (
                <button
                  type="button"
                  onClick={removeAudio}
                  className="text-[11px] text-muted-foreground hover:text-destructive flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
            </div>

            {isRecording ? (
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={stopRecording}
                  className="rounded-full h-9 px-3"
                >
                  <Square className="h-3.5 w-3.5 mr-1" /> Stop
                </Button>
                <div className="flex-1">
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-[width] duration-100"
                      style={{ width: `${Math.min(100, (recordElapsed / MAX_AUDIO_MS) * 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {(recordElapsed / 1000).toFixed(1)}s / 5.0s
                  </div>
                </div>
              </div>
            ) : draft.audioBlob && draft.audioUrl ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={togglePreview}
                  className="rounded-full h-9 px-3"
                >
                  {isPreviewPlaying ? <Pause className="h-3.5 w-3.5 mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                  {isPreviewPlaying ? "Pause" : "Play"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { removeAudio(); void startRecording(); }}
                  className="rounded-full h-9 px-3"
                >
                  <Mic className="h-3.5 w-3.5 mr-1" /> Re-record
                </Button>
                <span className="text-[11px] text-muted-foreground ml-auto">
                  {(draft.audioDurationMs / 1000).toFixed(1)}s
                </span>
                <audio
                  ref={previewAudioRef}
                  src={draft.audioUrl}
                  onEnded={() => setIsPreviewPlaying(false)}
                  className="hidden"
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void startRecording()}
                className="rounded-full h-9 px-3"
              >
                <Mic className="h-3.5 w-3.5 mr-1" /> Record voice
              </Button>
            )}

            <p className="text-[10px] text-muted-foreground leading-snug">
              Plays automatically when this sticker is sent. Add clear tags so it is easy to find later.
            </p>
          </div>
        )}

        <p className="text-[11px] text-muted-foreground leading-snug">
          Background is removed automatically after saving.
        </p>
      </div>

      {/* Sticky footer */}
      <div className="border-t border-border p-3 flex gap-2">
        <Button
          type="button"
          variant="ghost"
          className="flex-1 h-11"
          onClick={handleClose}
          disabled={draft.saveQueued}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="flex-1 h-11 gradient-primary text-primary-foreground"
          onClick={handleSave}
          disabled={!draft.file || draft.saveQueued}
        >
          {draft.saveQueued ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Saving…
            </>
          ) : (
            "Save sticker"
          )}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
